import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { Toast } from "../../components/Toast";

type Rgb = { r: number; g: number; b: number };

const isValidHex = (hex: string): boolean => {
  return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex.trim());
};

const normalizeHex = (hex: string): string => {
  const clean = hex.trim().replace("#", "");
  if (clean.length === 3) {
    return (
      "#" +
      clean
        .split("")
        .map((c) => c + c)
        .join("")
        .toUpperCase()
    );
  }
  return "#" + clean.toUpperCase();
};

const hexToRgb = (hex: string): Rgb | null => {
  const clean = hex.replace("#", "");
  const num = Number.parseInt(clean, 16);
  if (!Number.isFinite(num)) return null;
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = (rgb: Rgb): string => {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`.toUpperCase();
};

const invertRgb = (rgb: Rgb): Rgb => ({
  r: 255 - rgb.r,
  g: 255 - rgb.g,
  b: 255 - rgb.b,
});

export function ColorInverter() {
  const { t } = useTranslation();
  const [inputHex, setInputHex] = useState("#22D3EE");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const computed = useMemo(() => {
    if (!isValidHex(inputHex)) {
      return { isValid: false as const };
    }

    const originalHex = normalizeHex(inputHex);
    const originalRgb = hexToRgb(originalHex);
    if (!originalRgb) return { isValid: false as const };

    const invertedRgb = invertRgb(originalRgb);
    const invertedHex = rgbToHex(invertedRgb);

    return {
      isValid: true as const,
      originalHex,
      originalRgb,
      invertedHex,
      invertedRgb,
    };
  }, [inputHex]);

  const copyToClipboard = async (value: string, kind: "hex" | "rgb") => {
    try {
      await navigator.clipboard.writeText(value);
      setToastMessage(
        kind === "hex" ? t("colorInverter.copiedHex") : t("colorInverter.copiedRgb"),
      );
    } catch {
      setToastMessage(t("colorInverter.copyFailed"));
    }
  };

  const originalPreview = computed.isValid ? computed.originalHex : "#000000";
  const invertedPreview = computed.isValid ? computed.invertedHex : "#000000";

  const originalRgbText = computed.isValid
    ? `${computed.originalRgb.r}, ${computed.originalRgb.g}, ${computed.originalRgb.b}`
    : "";
  const invertedRgbText = computed.isValid
    ? `${computed.invertedRgb.r}, ${computed.invertedRgb.g}, ${computed.invertedRgb.b}`
    : "";

  return (
    <div className="color-inverter">
      <div className="ci-input">
        <label className="ci-label" htmlFor="ci-hex">
          {t("colorInverter.inputLabel")}
        </label>
        <input
          id="ci-hex"
          type="text"
          className="ci-hex-input"
          value={inputHex}
          onChange={(e) => setInputHex(e.target.value)}
          placeholder={t("colorInverter.inputPlaceholder")}
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        {!computed.isValid && (
          <div className="ci-error">{t("colorInverter.invalidHex")}</div>
        )}
      </div>

      <div className="ci-grid">
        <div className="ci-card">
          <div className="ci-card-title">{t("colorInverter.original")}</div>
          <div className="ci-preview" style={{ backgroundColor: originalPreview }} />

          <div className="ci-row">
            <div className="ci-row-label">{t("colorInverter.hex")}</div>
            <div className="ci-row-value">{computed.isValid ? computed.originalHex : "—"}</div>
            <button
              type="button"
              className="ci-copy-btn"
              onClick={() =>
                computed.isValid && copyToClipboard(computed.originalHex, "hex")
              }
              disabled={!computed.isValid}
              aria-label={t("colorInverter.copyHex")}
            >
              <Copy size={16} />
              {t("colorInverter.copyHex")}
            </button>
          </div>

          <div className="ci-row">
            <div className="ci-row-label">{t("colorInverter.rgb")}</div>
            <div className="ci-row-value">{computed.isValid ? originalRgbText : "—"}</div>
            <button
              type="button"
              className="ci-copy-btn"
              onClick={() => computed.isValid && copyToClipboard(originalRgbText, "rgb")}
              disabled={!computed.isValid}
              aria-label={t("colorInverter.copyRgb")}
            >
              <Copy size={16} />
              {t("colorInverter.copyRgb")}
            </button>
          </div>
        </div>

        <div className="ci-card">
          <div className="ci-card-title">{t("colorInverter.inverted")}</div>
          <div className="ci-preview" style={{ backgroundColor: invertedPreview }} />

          <div className="ci-row">
            <div className="ci-row-label">{t("colorInverter.hex")}</div>
            <div className="ci-row-value">{computed.isValid ? computed.invertedHex : "—"}</div>
            <button
              type="button"
              className="ci-copy-btn"
              onClick={() =>
                computed.isValid && copyToClipboard(computed.invertedHex, "hex")
              }
              disabled={!computed.isValid}
              aria-label={t("colorInverter.copyHex")}
            >
              <Copy size={16} />
              {t("colorInverter.copyHex")}
            </button>
          </div>

          <div className="ci-row">
            <div className="ci-row-label">{t("colorInverter.rgb")}</div>
            <div className="ci-row-value">{computed.isValid ? invertedRgbText : "—"}</div>
            <button
              type="button"
              className="ci-copy-btn"
              onClick={() => computed.isValid && copyToClipboard(invertedRgbText, "rgb")}
              disabled={!computed.isValid}
              aria-label={t("colorInverter.copyRgb")}
            >
              <Copy size={16} />
              {t("colorInverter.copyRgb")}
            </button>
          </div>
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}

